import React from "react";
import { formatMXN, imageUrl } from "../lib/api";
import { MapPin, Bed, Bathtub, Ruler, Heart } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useFavorites } from "../lib/favorites";
import { ShareMenu } from "./ShareMenu";

const propertyTypeLabel = {
  casa: "Casa",
  departamento: "Departamento",
  terreno: "Terreno",
  oficina: "Oficina",
  local: "Local",
};

// Luxury branded placeholder — never shows "No image".
// Rendered when property has no gallery yet.
const BrandedPlaceholder = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#1A1A1A] via-[#141414] to-[#0A0A0A]" aria-hidden="true">
    <div className="font-heading text-[#C8A24A]/30 text-5xl tracking-widest">RF</div>
    <div className="mt-2 text-xs uppercase tracking-[0.4em] text-[#C8A24A]/25">
      ROCAFUERTE
    </div>
  </div>
);

// Icon button base — 44×44 minimum touch target per WCAG + Apple HIG.
const IconButton = ({ children, onClick, active, testid, label, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    data-testid={testid}
    className={`w-11 h-11 rounded-full bg-[#0A0A0A]/70 backdrop-blur-md border border-[#C8A24A]/20 flex items-center justify-center transition-[border-color,background-color] duration-200 hover:border-[#C8A24A]/60 hover:bg-[#0A0A0A]/90 ${active ? "border-[#C8A24A]" : ""} ${className}`}
  >
    {children}
  </button>
);

export const PropertyCard = ({ property }) => {
  const { isFav, toggle } = useFavorites();
  const fav = isFav(property.id);
  const cover = property.images?.[0];
  const opLabel = property.operation === "renta" ? "En renta" : "En venta";

  return (
    <Link
      to={`/propiedades/${property.id}`}
      className="group block bg-[#1A1A1A] rounded-3xl overflow-hidden hover:shadow-[0_20px_60px_-20px_rgba(200,162,74,0.35)] transition-shadow duration-500"
      data-testid={`property-card-${property.id}`}
    >
      {/* Media */}
      <div className="relative aspect-[5/4] overflow-hidden">
        {cover ? (
          <img
            src={imageUrl(cover)}
            alt={property.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <BrandedPlaceholder />
        )}

        {/* Bottom gradient for legibility */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

        {/* Operation badge only — cleaner hierarchy */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 bg-[#0A0A0A]/80 backdrop-blur-md text-[#C8A24A] rounded-full text-xs font-semibold uppercase tracking-[0.15em] border border-[#C8A24A]/30">
            {opLabel}
          </span>
        </div>

        {/* Action buttons — 44×44 touch targets */}
        <div className="absolute top-4 right-4 flex gap-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          <ShareMenu property={property} variant="icon" className="w-11 h-11 rounded-full bg-[#0A0A0A]/70 backdrop-blur-md border-[#C8A24A]/20 hover:border-[#C8A24A]/60 hover:bg-[#0A0A0A]/90" />
          <IconButton
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(property.id); }}
            active={fav}
            testid={`favorite-btn-${property.id}`}
            label="Agregar a favoritos"
          >
            <Heart size={19} weight={fav ? "fill" : "regular"} className="text-[#C8A24A]" />
          </IconButton>
        </div>

        {/* Type — bottom-left as subtle overlay */}
        <div className="absolute bottom-4 left-4 z-10">
          <span className="text-xs uppercase tracking-[0.2em] text-[#C8A24A]/90 font-medium">
            {propertyTypeLabel[property.property_type] || property.property_type}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 md:p-7">
        <h3
          className="font-heading text-xl md:text-2xl leading-[1.15] text-[#C8A24A] line-clamp-2 tracking-tight"
          data-testid={`property-title-${property.id}`}
        >
          {property.title}
        </h3>

        <p className="mt-2 text-xs text-[#C8A24A]/60 flex items-center gap-1.5">
          <MapPin size={13} weight="fill" className="text-[#C8A24A]/50" />
          <span className="truncate">{property.city}, {property.state}</span>
        </p>

        {/* Metrics row */}
        <div className="mt-5 flex items-center gap-5 text-xs text-[#C8A24A]/70">
          {property.bedrooms > 0 && (
            <span className="flex items-center gap-1.5"><Bed size={15} weight="regular" /> {property.bedrooms}</span>
          )}
          {property.bathrooms > 0 && (
            <span className="flex items-center gap-1.5"><Bathtub size={15} weight="regular" /> {property.bathrooms}</span>
          )}
          {property.area_m2 > 0 && (
            <span className="flex items-center gap-1.5"><Ruler size={15} weight="regular" /> {property.area_m2} m²</span>
          )}
        </div>

        {/* Price — the visual anchor */}
        <div className="mt-6 pt-5 border-t border-[#C8A24A]/10 flex items-baseline justify-between">
          <span
            className="text-2xl md:text-3xl font-heading font-bold text-[#C8A24A] tracking-tight"
            data-testid={`property-price-${property.id}`}
          >
            {formatMXN(property.price)}
          </span>
          {property.operation === "renta" && (
            <span className="text-xs uppercase tracking-[0.2em] text-[#C8A24A]/50">/ mes</span>
          )}
        </div>
      </div>
    </Link>
  );
};
